import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Divider,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Update as UpdateIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import moment from 'moment';

/**
 * AuditEntryItem Component
 * Displays a single audit record with before/after values
 * Phase 8.2 Implementation
 */
const AuditEntryItem = ({ auditRecord, index, total }) => {
  const {
    changedByUsername = 'System',
    fieldLabel = 'Unknown Field',
    fieldName = '',
    oldValue = '',
    newValue = '',
    changeReason = '',
    changeSource = 'System',
    changedAt = new Date().toISOString(),
  } = auditRecord || {};

  const formattedTime = moment(changedAt).format('MMM DD, YYYY HH:mm:ss');
  const relativeTime = moment(changedAt).fromNow();

  // Determine icon and color based on change source
  const getSourceConfig = (source) => {
    const configs = {
      'Admin Portal': { color: '#0E6B6B', icon: '🔧' },
      'API': { color: '#1976d2', icon: '⚙️' },
      'System': { color: '#757575', icon: '🤖' },
    };
    return configs[source] || { color: '#757575', icon: '📝' };
  };

  const sourceConfig = getSourceConfig(changeSource);

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: `4px solid ${sourceConfig.color}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent>
        {/* Header: User and Timestamp */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: sourceConfig.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
              }}
            >
              {sourceConfig.icon}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {changedByUsername}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {relativeTime}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Chip
              label={changeSource}
              size="small"
              variant="outlined"
              sx={{
                borderColor: sourceConfig.color,
                color: sourceConfig.color,
                fontWeight: 500,
              }}
            />
            <Chip
              label={`${index} of ${total}`}
              size="small"
              variant="filled"
              sx={{
                backgroundColor: '#f5f5f5',
                color: '#666',
              }}
            />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Field Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
            {fieldLabel || fieldName}
          </Typography>
        </Box>

        {/* Before and After Values */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Old Value */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: '#ffebee',
                borderRadius: 1,
                border: '1px solid #ef5350',
              }}
            >
              <Typography variant="caption" color="error" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Previous Value
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  color: oldValue ? '#c62828' : '#999',
                  fontWeight: 500,
                }}
              >
                {oldValue || '(empty)'}
              </Typography>
            </Box>
          </Grid>

          {/* New Value */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: '#e8f5e9',
                borderRadius: 1,
                border: '1px solid #66bb6a',
              }}
            >
              <Typography variant="caption" color="success" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                New Value
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  color: newValue ? '#2e7d32' : '#999',
                  fontWeight: 500,
                }}
              >
                {newValue || '(empty)'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Change Reason */}
        {changeReason && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Change Reason
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                {changeReason}
              </Typography>
            </Box>
          </>
        )}

        {/* Timestamp Footer */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary">
            {formattedTime}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AuditEntryItem;
